import { getApp } from '#src/main'
import store from '#src/store'
import { createRouter, createWebHistory } from 'vue-router'

// API [R2D2]
import * as R2d2API from '#src/api/r2d2'
// API [CrowdSec]
import { listDecisions } from '#src/api/crowd-sec'
// API [LDAP Groups]
import { getAdGroup, getLdapGroup, searchAdUsers } from '#src/api/ldap-groups'
// API [LDAP Users]
import { getAdUser, getLdapUser } from '#src/api/ldap-users'
// API [Development statistics]
import * as devStatisticsAuthorsAPI from '#src/api/development-statistics/authors'
import * as devStatisticsProjectsAPI from '#src/api/development-statistics/projects'
import * as devStatisticsAPI from '#src/api/development-statistics/statistics'
// API [Message of the Day]
import * as MotdAPI from '#src/api/motd'

// API [Administration]
import { getUser, getUsers } from '#src/api/users'

// Utils
import { StatusTypes as ProjectStatusTypes } from '#src/utils/development-statistics/Projects'

// Layouts
import PageWithNavBarAndFooterLayout from '#src/layouts/PageWithNavBarAndFooterLayout.vue'
import { getCurrentGlobalProtectDevices } from '#src/api/global-protect'

// Pages
const HomePage = () => import('#src/pages/HomePage.vue')
const UnauthorizedPage = () => import('#src/pages/UnauthorizedPage.vue')
const PageNotFoundPage = () => import('#src/pages/PageNotFoundPage.vue')
// R2D2 Pages
const R2d2 = () => import('#src/pages/r2d2/R2d2.vue')
const ListR2D2Databases = () => import('#src/pages/r2d2/ListR2D2Databases.vue')
// CrowdSec pages
const CrowdSec = () => import('#src/pages/crowd-sec/CrowdSec.vue')

// Ldap page components
const LdapSearch = () => import('#src/pages/ldap-search/LdapSearch.vue')
const LDAPGroup = () => import('#src/pages/ldap-search/LdapGroup.vue')
const LDAPUser = () => import('#src/pages/ldap-search/LdapUser.vue')
const UserDeviceList = () => import('#src/pages/ldap-search/UserDeviceList.vue')

// Development statistics pages
const DevelopmentStatisticsHomePage = () => import('#src/pages/development-statistics/HomePage.vue')
const GlobalStatisticsPage = () => import('#src/pages/development-statistics/statistics/GlobalStatisticsPage.vue')
const ProjectStatisticsPage = () => import('#src/pages/development-statistics/statistics/ProjectStatisticsPage.vue')
const ManagementPage = () => import('#src/pages/development-statistics/management/ManagementPage.vue')
const ProjectsManagementPage = () => import('#src/pages/development-statistics/management/projects/ProjectsManagementPage.vue')
const ProjectManagementPage = () => import('#src/pages/development-statistics/management/projects/ProjectManagementPage.vue')
const AuthorsManagementPage = () => import('#src/pages/development-statistics/management/authors/AuthorsManagementPage.vue')
const AuthorManagementPage = () => import('#src/pages/development-statistics/management/authors/AuthorManagementPage.vue')
const CreateAuthorPage = () => import('#src/pages/development-statistics/management/authors/CreateAuthorPage.vue')
const EditAuthorPage = () => import('#src/pages/development-statistics/management/authors/EditAuthorPage.vue')
// Message of the Day pages
const MotdPage = () => import('#src/pages/motd/MotdPage.vue')
const MotdVersionHistoryPage = () => import('#src/pages/motd/MotdVersionHistoryPage.vue')
const MotdUserAcksPage = () => import('#src/pages/motd/MotdUserAcksPage.vue')
const MotdUserSearchPage = () => import('#src/pages/motd/MotdUserSearchPage.vue')
const motdUserSearchResultsPage = () => import('#src/pages/motd/MotdUserSearchResultsPage.vue')
const MotdNewPage = () => import('#src/pages/motd/MotdNewPage.vue')
const MotdEditPage = () => import('#src/pages/motd/MotdEditPage.vue')
const MotdMessageAcksPage = () => import('#src/pages/motd/MotdMessageAcksPage.vue')
const MotdMessagePage = () => import('#src/pages/motd/MotdMessagePage.vue')
// Endpoint Logs
const EndpointLogsPage = () => import('#src/pages/endpoint-logs/EndpointLogsPage.vue')
// Administration pages
const UsersAdministrationPage = () => import('#src/pages/admin/users/UsersAdministrationPage.vue')
const UserAdministrationPage = () => import('#src/pages/admin/users/UserAdministrationPage.vue')
// DML Viewer pages
const DmlViewerPage = () => import('#src/pages/dml-viewer/DmlViewerPage.vue')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      component: PageWithNavBarAndFooterLayout,
      redirect: { name: 'HomePage', query: { active: 'true' } },
      children: [
        {
          path: '/unauthorized',
          name: 'UnauthorizedPage',
          component: UnauthorizedPage,
          meta: {
            public: true
          }
        },
        {
          path: '/',
          name: 'HomePage',
          component: HomePage
        },
        {
          path: '/r2d2/',
          name: 'R2d2',
          component: R2d2,
          meta: {
            'porg-auth': {
              roles: ['user', 'r2d2']
            }
          },
          props: route => ({
            projects: route.meta.projects
          }),
          async beforeEnter (to, from, next) {
            to.meta.projects = await R2d2API.listAvailableDatabaseTypes()
            next()
          }
        },
        {
          path: '/r2d2/:id',
          name: 'ListR2D2Databases',
          component: ListR2D2Databases,
          meta: {
            'porg-auth': {
              roles: ['user', 'r2d2']
            }
          },
          props: route => ({
            availableDatabases: route.meta.availableDatabases,
            runningDatabases: route.meta.runningDatabases,
            project: route.meta.project,
            id: Number(route.params.id)
          }),
          async beforeEnter (to, from, next) {
            const [project, availableDatabases, runningDatabases] = await Promise.all([
              R2d2API.getProject(to.params.id),
              R2d2API.listAvailableDatabases(to.params.id),
              R2d2API.listRunningDatabases(to.params.id)
            ])
            to.meta.project = project
            to.meta.availableDatabases = availableDatabases
            to.meta.runningDatabases = runningDatabases
            next()
          }
        },
        {
          path: '/crowd-sec/',
          name: 'CrowdSec',
          component: CrowdSec,
          meta: {
            'porg-auth': {
              roles: ['user', 'crowd-sec']
            }
          },
          props: route => ({
            initialDecisionsGroupedByIp: route.meta.initialDecisionsGroupedByIp
          }),
          async beforeEnter (to, from, next) {
            to.meta.initialDecisionsGroupedByIp = await listDecisions()
            next()
          }
        },
        {
          path: '/ldap/search',
          name: 'LdapSearch',
          component: LdapSearch,
          meta: {
            'porg-auth': {
              roles: ['user', 'ldap']
            }
          }
        },
        {
          path: '/ldap/ad/groups/:name',
          name: 'AdGroup',
          component: LDAPGroup,
          meta: {
            'porg-auth': {
              roles: ['user', 'ldap']
            }
          },
          props: route => ({
            group: route.meta.group
          }),
          async beforeEnter (to, from, next) {
            const group = await getAdGroup(to.params.name)
            to.meta.group = group
            next()
          }
        },
        {
          path: '/ldap/ad/users/:uid',
          name: 'AdUser',
          component: LDAPUser,
          meta: {
            'porg-auth': {
              roles: ['user', 'ldap']
            }
          },
          props: route => {
            return {
              user: route.meta.user
            }
          },
          async beforeEnter (to, from, next) {
            const user = await getAdUser(to.params.uid)
            to.meta.user = user
            next()
          }
        },

        {
          path: '/ldap/ad/users/:username/devices',
          name: 'UserDeviceList',
          component: UserDeviceList,
          meta: {
            'porg-auth': {
              roles: ['user', 'ldap']
            }
          },
          props: route => {
            return {
              user: route.meta.user,
              gateways: route.meta.gateways
            }
          },
          async beforeEnter (to, from, next) {
            const [user, gateways] = await Promise.all([getAdUser(to.params.username), getCurrentGlobalProtectDevices(to.params.username)])
            to.meta.user = user
            to.meta.gateways = gateways
            next()
          }
        },
        {
          path: '/ldap/openldap/groups/:name',
          name: 'LDAPGroup',
          component: LDAPGroup,
          meta: {
            'porg-auth': {
              roles: ['user', 'ldap']
            }
          },
          props: route => ({
            group: route.meta.group
          }),
          async beforeEnter (to, from, next) {
            const group = await getLdapGroup(to.params.name)
            to.meta.group = group
            next()
          }
        },
        {
          path: '/ldap/openldap/users/:uid',
          name: 'LDAPUser',
          component: LDAPUser,
          meta: {
            'porg-auth': {
              roles: ['user', 'ldap']
            }
          },
          props: route => {
            return {
              user: route.meta.user
            }
          },
          async beforeEnter (to, from, next) {
            const user = await getLdapUser(to.params.uid)
            to.meta.user = user
            next()
          }
        },
        {
          path: '/development-statistics',
          name: 'DevelopmentStatistics',
          component: DevelopmentStatisticsHomePage,
          meta: {
            'porg-auth': {
              roles: ['user', 'development-statistics']
            }
          },
          props: route => ({
            metadata: route.meta.metadata,
            projectsList: route.meta.projectsList
          }),
          beforeEnter: async (to, from, next) => {
            const [metadata, projectsList] = await Promise.all([
              devStatisticsAPI.getMetadata(),
              devStatisticsProjectsAPI.getActiveProjects()
            ])
            to.meta.metadata = metadata
            to.meta.projectsList = projectsList
            next()
          }
        },
        {
          path: '/development-statistics/statistics',
          name: 'StatisticsPage',
          redirect: { name: 'GlobalStatisticsPage' }
        },
        {
          path: '/development-statistics/statistics/global',
          name: 'GlobalStatisticsPage',
          component: GlobalStatisticsPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'development-statistics']
            }
          },
          props: route => ({
            statistics: route.meta.statistics,
            start: route.query.start,
            end: route.query.end
          }),
          beforeEnter: async (to, from, next) => {
            const statistics = await devStatisticsAPI.getGlobalStatistics({
              start: to.query.start,
              end: to.query.end
            })
            to.meta.statistics = statistics
            next()
          }
        },
        {
          path: '/development-statistics/statistics/project/:id',
          name: 'ProjectStatisticsPage',
          component: ProjectStatisticsPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'development-statistics']
            }
          },
          props: route => ({
            project: route.meta.project,
            statistics: route.meta.statistics,
            start: route.query.start,
            end: route.query.end
          }),
          beforeEnter: async (to, from, next) => {
            const [project, statistics] = await Promise.all([
              devStatisticsProjectsAPI.getProject({ id: to.params.id }),
              devStatisticsAPI.getProjectStatistics({
                id: to.params.id,
                start: to.query.start,
                end: to.query.end
              })
            ])
            to.meta.project = project
            to.meta.statistics = statistics
            next()
          }
        },
        {
          path: '/development-statistics/manage',
          component: ManagementPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'development-statistics']
            }
          },
          children: [
            {
              path: '',
              name: 'ManagementPage',
              redirect: { name: 'ProjectsManagementPage' }
            },
            {
              path: 'projects',
              name: 'ProjectsManagementPage',
              component: ProjectsManagementPage,
              props: route => ({
                projects: route.meta.projects,
                totalItems: route.meta.totalItems,
                totalActive: route.meta.totalActive,
                totalInactive: route.meta.totalInactive,
                query: route.query.q,
                status: route.query.status,
                page: route.meta.page,
                perPage: route.meta.perPage
              }),
              beforeEnter: async (to, from, next) => {
                // Validate status from query
                if (to.query.status && !Object.values(ProjectStatusTypes).includes(to.query.status)) {
                  return next('/404')
                }

                // Default pagination items per page
                to.meta.perPage = 7
                to.meta.page = Number(to.query.page) || 1

                // Fetch projects
                const { totalItems, totalActive, totalInactive, items } = await devStatisticsProjectsAPI.getProjects({
                  search: to.query.q,
                  status: to.query.status,
                  page: to.meta.page,
                  perPage: to.meta.perPage
                })
                to.meta.projects = items
                to.meta.totalItems = totalItems
                to.meta.totalActive = totalActive
                to.meta.totalInactive = totalInactive
                next()
              }
            },
            {
              path: 'projects/:id',
              name: 'ProjectManagementPage',
              component: ProjectManagementPage,
              props: route => ({
                project: route.meta.project
              }),
              beforeEnter: async (to, from, next) => {
                const project = await devStatisticsProjectsAPI.getProject({ id: to.params.id })
                to.meta.project = project
                next()
              }
            },
            {
              path: 'authors',
              name: 'AuthorsManagementPage',
              props: route => ({
                authors: route.meta.authors,
                totalItems: route.meta.totalItems,
                query: route.query.q,
                page: route.meta.page,
                perPage: route.meta.perPage
              }),
              component: AuthorsManagementPage,
              beforeEnter: async (to, from, next) => {
                // Default pagination items per page
                to.meta.perPage = 7
                to.meta.page = Number(to.query.page) || 1

                // Fetch authors
                const { totalItems, items } = await devStatisticsAuthorsAPI.getAuthors({
                  search: to.query.q,
                  page: to.meta.page,
                  perPage: to.meta.perPage
                })
                to.meta.authors = items
                to.meta.totalItems = totalItems
                next()
              }
            },
            {
              path: 'authors/create',
              name: 'CreateAuthorPage',
              component: CreateAuthorPage
            },
            {
              path: 'authors/:id',
              name: 'AuthorManagementPage',
              component: AuthorManagementPage,
              props: route => ({
                author: route.meta.author
              }),
              beforeEnter: async (to, from, next) => {
                const author = await devStatisticsAuthorsAPI.getAuthor({ id: to.params.id })
                to.meta.author = author
                next()
              }
            },
            {
              path: 'authors/:id/edit',
              name: 'EditAuthorPage',
              component: EditAuthorPage,
              props: route => ({
                author: route.meta.author
              }),
              beforeEnter: async (to, from, next) => {
                const author = await devStatisticsAuthorsAPI.getAuthor({ id: to.params.id })
                to.meta.author = author
                next()
              }
            }
          ]
        },
        {
          path: '/motd',
          name: 'MotdPage',
          component: MotdPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          }
        },
        {
          path: '/motd/:messageId',
          name: 'MotdMessagePage',
          component: MotdMessagePage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          },
          props: route => {
            return {
              message: route.meta.message
            }
          },
          async beforeEnter (to, from, next) {
            const message = await MotdAPI.getMotd(to.params.messageId)
            to.meta.message = message
            next()
          }
        },
        {
          path: '/motd/:messageId/versions',
          name: 'MotdVersionHistoryPage',
          component: MotdVersionHistoryPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          },
          props: route => {
            return {
              message: route.meta.message
            }
          },
          async beforeEnter (to, from, next) {
            const message = await MotdAPI.getMotd(to.params.messageId)
            to.meta.message = message
            next()
          }
        },
        {
          path: '/motd/:messageId/acks',
          name: 'MotdMessageAcksPage',
          component: MotdMessageAcksPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          }
        },
        {
          path: '/motd/:messageId/edit',
          name: 'MotdEditPage',
          component: MotdEditPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          },
          props: route => {
            return {
              message: route.meta.message
            }
          },
          async beforeEnter (to, from, next) {
            const message = await MotdAPI.getMotd(to.params.messageId)
            to.meta.message = message
            next()
          }
        },
        {
          path: '/motd/new',
          name: 'MotdNewPage',
          component: MotdNewPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          }
        },
        {
          path: '/motd/acks/search',
          name: 'MotdUserSearchPage',
          component: MotdUserSearchPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          }
        },
        {
          path: '/motd/acks/search/results',
          name: 'MotdUserSearchResultsPage',
          component: motdUserSearchResultsPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          },
          props: route => ({
            query: route.query.query,
            users: route.meta.users
          }),
          async beforeEnter (to, from, next) {
            const users = await searchAdUsers(to.query.query)
            to.meta.users = users
            next()
          }
        },
        {
          path: '/motd/acks/:uid',
          name: 'MotdUserAcksPage',
          component: MotdUserAcksPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'motd']
            }
          },
          props: route => {
            return {
              user: route.meta.user,
              acks: route.meta.acks
            }
          },
          async beforeEnter (to, from, next) {
            const user = await getLdapUser(to.params.uid)
            const acks = await MotdAPI.getUserAcks(to.params.uid)
            to.meta.user = user
            to.meta.acks = acks
            next()
          }
        },
        {
          path: '/endpoint-logs',
          name: 'EndpointLogsPage',
          component: EndpointLogsPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'admin']
            }
          }
        },
        {
          path: '/admin/users',
          name: 'UsersAdministrationPage',
          component: UsersAdministrationPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'admin']
            }
          },
          props: route => ({
            users: route.meta.users,
            totalItems: route.meta.totalItems,
            query: route.query.q,
            page: route.meta.page,
            perPage: route.meta.perPage
          }),
          beforeEnter: async (to, from, next) => {
            // Default pagination items per page
            to.meta.perPage = 7
            to.meta.page = Number(to.query.page) || 1

            // Fetch users
            const { totalItems, items } = await getUsers({
              search: to.query.q,
              page: to.meta.page,
              perPage: to.meta.perPage
            })
            to.meta.users = items
            to.meta.totalItems = totalItems
            next()
          }
        },
        {
          path: '/admin/users/:username',
          name: 'UserAdministrationPage',
          component: UserAdministrationPage,
          meta: {
            'porg-auth': {
              roles: ['user', 'admin']
            }
          },
          props: route => ({
            user: route.meta.user
          }),
          beforeEnter: async (to, from, next) => {
            const user = await getUser({ username: to.params.username })
            to.meta.user = user
            next()
          }
        },
        {
          path: '/dml-viewer',
          name: 'DmlViewerPage',
          component: DmlViewerPage,
          meta: {
            'porg-auth': {
              roles: ['user']
            }
          }
        }
      ]
    },
    {
      path: '/logout',
      name: 'Logout'
    },
    {
      path: '/:pathMatch(.*)',
      component: PageWithNavBarAndFooterLayout,
      children: [{
        path: '',
        name: 'PageNotFound',
        component: PageNotFoundPage
      }]
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  store.dispatch('setProgressBar', 10)

  // we want this here and not in router/index.js because we want vue-porg's
  // permission system to run before this global navigation guard
  if (from.name && to.path === from.path) {
    // don't run when only the query params change
    return next()
  }

  const app = getApp()
  if (!hasPersistedNotification()) {
    app.clearNotification()
  }

  if (to.path === '/logout' && app.$auth.isLogged()) {
    app.$auth.logout()
    return
  }

  if (!to.meta.public) {
    await store.dispatch('fetchProfile')
    store.dispatch('setProgressBar', 60)
  }

  next()
})

router.afterEach((to, from, next) => {
  store.dispatch('completeProgressBar')
})

const hasPersistedNotification = () => {
  if (!store.state.topMessage.active) {
    return false
  }
  return store.state.topMessage.persist
}

export default router
